import React, { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MuiButton from '@mui/material/Button';
import LandingLayout from './components/LandingLayout';
import { THINKCERCA_PUBLISHER, CurriculumPublisher, PublisherLesson } from './data/thinkcerca-publisher';
import PressoLessonPreview from './PressoLessonPreview';
import { CurriculumLesson } from './data/peninsula-district';

const SHADOW = '4px 4px 0px #1e1e1e';
const SHADOW_SM = '2px 2px 0px #1e1e1e';
const PRESSTO_PURPLE = '#5C4FB8';
const CERCA_TEAL = '#00897B';
const CREAM = '#F2EEE5';

const PUBLISHER_BY_SLUG: Record<string, CurriculumPublisher> = {
  thinkcerca: THINKCERCA_PUBLISHER,
};

const PLAN_LABELS: Record<string, string> = {
  'op-ed': 'Opinion', 'sequence': 'Sequence', 'cause-effect': 'Cause and Effect',
  'compare-contrast': 'Compare and Contrast', 'retell-the-narrative': 'Retell the Narrative',
  'personal-narrative': 'Personal Narrative', 'description': 'Description',
  'problem-solution': 'Problem and Solution', 'basic': 'Basic',
};

const CERCA_VALUE_PROPS = [
  {
    icon: '📐',
    title: 'Framework → writing space',
    body: 'ThinkCERCA teaches students how to argue. Pressto is where they write it — a structured digital workspace with planning tools, word banks, and sentence scaffolds.',
  },
  {
    icon: '🧩',
    title: 'CERCA-aligned writing plans',
    body: 'Every Pressto lesson carries an Opinion writing plan that mirrors the CERCA structure: claim, evidence, reasoning, counterargument, audience.',
  },
  {
    icon: '✨',
    title: 'AI Writing Buddy',
    body: 'Helps students find their own words and keep going — it asks questions and prompts thinking, but never writes it for them.',
  },
  {
    icon: '📰',
    title: 'Publish their work',
    body: 'Students can publish finished arguments to a class or school newspaper — giving CERCA writing a real audience.',
  },
];

function CTAButton({ children, onClick, dark = true }: { children: React.ReactNode; onClick: () => void; dark?: boolean }) {
  return (
    <MuiButton
      variant="contained"
      onClick={onClick}
      sx={{
        backgroundColor: dark ? '#2C2C2C' : '#FFE082',
        color: dark ? '#fff' : '#2C2C2C',
        fontFamily: 'Montserrat, Inter, sans-serif', fontWeight: 700, fontSize: '1rem',
        textTransform: 'none', borderRadius: '6px', border: '2px solid #1e1e1e',
        boxShadow: SHADOW, px: 4, py: 1.5,
        '&:hover': { boxShadow: SHADOW_SM, transform: 'translate(2px,2px)' },
        transition: 'all 100ms',
      }}
    >
      {children}
    </MuiButton>
  );
}

function Chip({ label, bg, color, small }: { label: string; bg: string; color: string; small?: boolean }) {
  return (
    <Box sx={{
      backgroundColor: bg, color, borderRadius: '4px', px: small ? 0.75 : 1, py: 0.3,
      fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: small ? '0.6rem' : '0.65rem',
      textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap',
    }}>{label}</Box>
  );
}

function LessonCard({ lesson, onClick }: { lesson: PublisherLesson; onClick: () => void }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        borderRadius: '8px', border: '2px solid #1e1e1e',
        boxShadow: SHADOW_SM, backgroundColor: '#fff', overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        cursor: 'pointer',
        '&:hover': { boxShadow: '2px 2px 0px #1e1e1e', transform: 'translate(2px,2px)' },
        transition: 'all 100ms',
      }}
    >
      <Box sx={{ height: 6, backgroundColor: CERCA_TEAL }} />
      <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', gap: 0.75, mb: 1, flexWrap: 'wrap' }}>
          <Chip label={lesson.grade} bg="#2C2C2C" color="#FFE082" />
          {PLAN_LABELS[lesson.plan] && <Chip label={PLAN_LABELS[lesson.plan]} bg={CERCA_TEAL} color="#fff" />}
        </Box>
        <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '0.9rem', color: '#1e1e1e', mb: 0.75, lineHeight: 1.2 }}>
          {lesson.title}
        </Typography>
        <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.8rem', color: '#666', lineHeight: 1.5, flex: 1, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {lesson.prompt}
        </Typography>
        <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '0.7rem', color: CERCA_TEAL, mt: 1.25, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Preview →
        </Typography>
      </Box>
    </Box>
  );
}

export default function CurriculumPublisherPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const publisher = slug ? PUBLISHER_BY_SLUG[slug] : undefined;

  const [preview, setPreview] = useState<PublisherLesson | null>(null);

  const byUnit = useMemo(() => {
    const map: Record<string, PublisherLesson[]> = {};
    (publisher?.lessons || []).forEach((l) => {
      (map[l.theme] = map[l.theme] || []).push(l);
    });
    return map;
  }, [publisher]);

  if (!publisher) {
    return (
      <LandingLayout>
        <Box sx={{ textAlign: 'center', py: 16 }}>
          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '1.5rem' }}>
            Publisher page not found
          </Typography>
        </Box>
      </LandingLayout>
    );
  }

  const unitLabel = (theme: string) => theme.replace(/^ThinkCERCA · /, '');

  return (
    <LandingLayout>
      {/* Hero */}
      <Box sx={{ backgroundColor: CREAM, borderBottom: '2px solid #1e1e1e', py: { xs: 8, md: 12 }, px: { xs: 3, md: 6 } }}>
        <Box sx={{ maxWidth: 880, mx: 'auto', textAlign: 'center' }}>
          {/* Partner badge */}
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5, backgroundColor: '#fff', border: '2px solid #1e1e1e', borderRadius: '6px', px: 2, py: 1, mb: 4, boxShadow: SHADOW_SM }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: CERCA_TEAL }} />
            <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Publisher Partnership · Grades {publisher.gradeRange}
            </Typography>
          </Box>

          <Typography variant="h1" sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: { xs: '2.2rem', md: '3.2rem' }, lineHeight: 1.1, color: '#2C2C2C', mb: 2 }}>
            <Box component="span" sx={{ color: PRESSTO_PURPLE }}>Pressto</Box>
            {' + '}
            <Box component="span" sx={{ color: CERCA_TEAL }}>ThinkCERCA</Box>
          </Typography>

          <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: { xs: '1.1rem', md: '1.3rem' }, color: '#444', lineHeight: 1.6, mb: 2, fontWeight: 600 }}>
            ThinkCERCA teaches students how to argue.
          </Typography>
          <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: { xs: '1.1rem', md: '1.3rem' }, color: PRESSTO_PURPLE, lineHeight: 1.6, mb: 4, fontWeight: 700 }}>
            Pressto is where they write it.
          </Typography>

          <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '1rem', color: '#666', lineHeight: 1.6, mb: 5, maxWidth: 640, mx: 'auto' }}>
            {publisher.lessonCount} ready-to-use argumentative writing activities across {publisher.unitCount} thematic units — each one aligned to the CERCA framework and built for grades {publisher.gradeRange}.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <CTAButton onClick={() => navigate('/contact')}>Let&apos;s partner →</CTAButton>
            <CTAButton dark={false} onClick={() => navigate('/register')}>Try Pressto free</CTAButton>
          </Box>
        </Box>
      </Box>

      {/* The case for partnership */}
      <Box sx={{ backgroundColor: '#fff', py: { xs: 6, md: 9 }, px: { xs: 3, md: 6 } }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '1.6rem', color: '#1e1e1e', mb: 1.5, textAlign: 'center' }}>
            The argument framework needs a writing workspace
          </Typography>
          <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '1rem', color: '#666', lineHeight: 1.6, mb: 5, textAlign: 'center', maxWidth: 700, mx: 'auto' }}>
            ThinkCERCA&apos;s CERCA framework is one of the most rigorous approaches to argumentative writing in K-12. Pressto provides the structured digital workspace where students turn that framework into real, publishable writing.
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
            {CERCA_VALUE_PROPS.map((f) => (
              <Box key={f.title} sx={{ border: '2px solid #1e1e1e', borderRadius: '8px', boxShadow: SHADOW_SM, p: 3, display: 'flex', gap: 2 }}>
                <Box component="span" sx={{ fontSize: '1.6rem', lineHeight: 1 }}>{f.icon}</Box>
                <Box>
                  <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '1rem', color: '#1e1e1e', mb: 0.5 }}>{f.title}</Typography>
                  <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.9rem', color: '#555', lineHeight: 1.5 }}>{f.body}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Stats bar */}
      <Box sx={{ backgroundColor: '#EDE9F7', borderTop: '2px solid #1e1e1e', borderBottom: '2px solid #1e1e1e', py: 4, px: { xs: 3, md: 6 } }}>
        <Box sx={{ maxWidth: 900, mx: 'auto', display: 'flex', justifyContent: 'center', gap: { xs: 4, md: 8 }, flexWrap: 'wrap' }}>
          {[
            { n: publisher.lessonCount, label: 'aligned writing activities' },
            { n: publisher.unitCount, label: 'ThinkCERCA unit themes' },
            { n: '3–8', label: 'grade range', isText: true },
          ].map((s) => (
            <Box key={s.label} sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '2.4rem', color: PRESSTO_PURPLE, lineHeight: 1 }}>
                {s.isText ? s.n : `${s.n}+`}
              </Typography>
              <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', color: '#666', mt: 0.5 }}>
                {s.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Lesson cards by unit */}
      <Box sx={{ backgroundColor: '#F7F8FA', py: { xs: 6, md: 9 }, px: { xs: 3, md: 6 } }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '1.5rem', color: '#1e1e1e', mb: 1, textAlign: 'center' }}>
            One writing activity per ThinkCERCA unit theme
          </Typography>
          <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.9rem', color: '#888', mb: 5, textAlign: 'center' }}>
            Two grade levels per unit — grades 3–5 and 6–8 — each with a CERCA-style argumentative prompt and scaffolded writing plan.
          </Typography>
          {publisher.units.map((unit) => (
            <Box key={unit} sx={{ mb: 5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '2px', backgroundColor: CERCA_TEAL, flexShrink: 0 }} />
                <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '1.1rem', color: '#1e1e1e' }}>
                  {unitLabel(unit)}
                </Typography>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                {(byUnit[unit] || []).map((lesson, i) => (
                  <LessonCard key={i} lesson={lesson} onClick={() => setPreview(lesson)} />
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Bottom CTA */}
      <Box sx={{ backgroundColor: '#2C2C2C', borderTop: '2px solid #1e1e1e', py: { xs: 8, md: 10 }, px: 3, textAlign: 'center' }}>
        <Box sx={{ maxWidth: 640, mx: 'auto' }}>
          <Typography variant="h2" sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: { xs: '1.7rem', md: '2.4rem' }, color: '#fff', mb: 2, lineHeight: 1.15 }}>
            Let&apos;s build the complete<br />CERCA writing experience together.
          </Typography>
          <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '1.05rem', color: 'rgba(255,255,255,0.7)', mb: 5 }}>
            ThinkCERCA&apos;s argumentation framework + Pressto&apos;s writing workspace = a K-8 program where every student argues, writes, and publishes.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <CTAButton dark={false} onClick={() => navigate('/contact')}>Get in touch →</CTAButton>
            <MuiButton
              variant="outlined"
              onClick={() => navigate('/register')}
              sx={{
                color: '#fff', borderColor: 'rgba(255,255,255,0.4)',
                fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '1rem',
                textTransform: 'none', borderRadius: '6px', px: 4, py: 1.5,
                '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.08)' },
              }}
            >
              Try Pressto free
            </MuiButton>
          </Box>
        </Box>
      </Box>

      {preview && (
        <PressoLessonPreview
          lesson={preview as unknown as CurriculumLesson}
          onClose={() => setPreview(null)}
          onRegister={() => navigate('/register')}
          onPricing={() => navigate('/contact')}
        />
      )}
    </LandingLayout>
  );
}
